/*
 * Instant Task Alert pop-up (wireframe 03).
 *
 * A corner overlay mounted once in the app shell so an instant task reaches a
 * rep wherever they are in Hridaya, not only on the sales task list. It is fed
 * by two sources that must agree:
 *
 *   1. The `instant_task` / `instant_task_cleared` relay packets, delivered over
 *      the same chat WebSocket the fresh-lead notification already uses.
 *   2. GET /v2/sales_task_list/instant_pending, called on mount and whenever the
 *      tab regains focus.
 *
 * REST reconcile is not belt-and-braces: relay packets are `deliveryType:
 * "oneshot"` and are never replayed, so a rep whose tab was closed (or asleep)
 * when the task fired would otherwise never learn about it — in either
 * direction. The reconcile is therefore AUTHORITATIVE: a task it does not list
 * is removed, which is the only way a missed `instant_task_cleared` packet ever
 * stops showing a dead alert. The single exception is a packet that arrived
 * after the request was issued, which the server could not have known about —
 * see arrived_at_ms in instant_alert_entries.ts — and a response the server's
 * row cap may have truncated, which proves nothing about what it left out. The
 * server omits claimed rows, so a task claimed elsewhere leaves by absence (or
 * by its `instant_task_cleared` packet when the response is capped).
 *
 * Visibility is derived, never stored: the pop-up shows when at least one
 * pending task has NOT been dismissed. That is what makes a new instant task
 * re-open a pop-up the rep dismissed a minute ago while older tasks are still
 * pending — the PRD's explicit requirement, and the reason dismissal is a set of
 * uuids rather than a boolean (see instant_popup_storage.ts).
 *
 * `is_sales` comes from the server with the pending list rather than being
 * derived from a client-side role check, the same capability-boolean pattern the
 * sales list already uses for `can_edit`. The relay fan-out reaches every CMS
 * session; this boolean is what keeps the alert to sales.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import type RelayItem from '../../../myt_chat/chat/packets/RelayItem';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { get_api as getApi, post_api as postApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import {
  elapsed_ms as elapsedMs,
  format_elapsed as formatElapsed,
  short_label as shortLabel,
  timer_color_css as timerColorCss
} from './instant';
import { byAge, mergeEntries } from './instant_alert_entries';
import type { InstantEntry } from './instant_alert_entries';
import { DEFAULT_CORNER, isCorner, readPopupState, writePopupState } from './instant_popup_storage';
import type { Corner } from './instant_popup_storage';

const LIST_PATH = '/sales-task-list';
const FORM_PATH = '/sales-task-form';
// The server clamps `limit` to this same value, so a response of exactly this
// many rows may have been truncated and cannot be treated as the complete
// pending set. One constant so the request and that test can never disagree.
const PENDING_LIMIT = 50;
const PENDING_URL = `/v2/sales_task_list/instant_pending?limit=${PENDING_LIMIT}`;

// Wave 8 instrumentation. The other four PRD events (created / opened /
// completed / auto-completed) are already timestamp columns on the task row;
// only these two have no other source, so this is the whole telemetry surface.
const ALERT_EVENT_URL = '/v2/sales_task_list/alert_event';
const EVENT_SHOWN = 'shown';
const EVENT_DISMISSED = 'dismissed';

// Fire-and-forget: the pop-up must never wait on, or fail because of,
// telemetry. A rejected POST is swallowed — a lost row is a slightly wrong
// dismiss rate, a thrown one would be an alert the rep does not get.
type AlertEventType = typeof EVENT_SHOWN | typeof EVENT_DISMISSED;

// goserver /instant_pending `data`.
interface PendingResponse {
  data?: { data?: { items?: InstantEntry[]; is_sales?: boolean } };
}

interface Position {
  left: number;
  top: number;
}

const sendAlertEvent = (taskUuid: string, eventType: AlertEventType): void => {
  if (!taskUuid) return;
  postApi(getApiUrl(ALERT_EVENT_URL), { task_uuid: taskUuid, event_type: eventType }, true).catch(
    () => {}
  );
};

// Matches the list's and the banner's clock so the same task reads the same
// elapsed time in all three places.
const TICK_MS = 1000;

// Both `focus` and `visibilitychange` fire on one alt-tab back, and this
// component is mounted in the app shell for every CMS user on every page.
// Mirrors FOCUS_REFRESH_STALE_MS on the sales list.
const RECONCILE_STALE_MS = 5000;

// The mount reconcile is the only thing that can set `is_sales`, and the
// component renders nothing without it, so a single failure would silence the
// alert for the life of the page. Bounded so a hard-down endpoint is not polled
// forever.
const MOUNT_RECONCILE_ATTEMPTS = 3;
const MOUNT_RECONCILE_RETRY_MS = 5000;

// Distance from the viewport edge in each corner. Large enough to clear the
// page's own chrome, small enough that the pop-up still reads as "in the
// corner".
const EDGE_GAP_PX = 24;

const POPUP_WIDTH_PX = 320;

const cornerStyle = (corner: Corner): CSSProperties => {
  const [vertical, horizontal] = (isCorner(corner) ? corner : DEFAULT_CORNER).split('-');
  return {
    [vertical]: EDGE_GAP_PX,
    [horizontal]: EDGE_GAP_PX
  };
};

// Which corner a dropped pop-up belongs to: whichever half of the viewport its
// centre landed in. Snapping (rather than free positioning) is deliberate — the
// PRD asks for four corners, and a free position would eventually cover the page
// content the rep is reading.
const nearestCorner = (
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  viewport: { width: number; height: number }
): Corner => {
  const centreX = rect.left + rect.width / 2;
  const centreY = rect.top + rect.height / 2;
  const vertical = centreY < viewport.height / 2 ? 'top' : 'bottom';
  const horizontal = centreX < viewport.width / 2 ? 'left' : 'right';
  return `${vertical}-${horizontal}`;
};

// The assignment line under each entry. The assignee is whoever round-robin
// picked; claiming on open reassigns, so this is "who has it now", not "who must
// do it" — every rep in the broadcast may act on it.
const assignedLine = (entry: InstantEntry): string =>
  entry.assigned_name ? `assigned to ${entry.assigned_name}` : 'unassigned';

function InstantAlertPopup() {
  const cmsUserUuid = getAuthUserInfoOrDefault().uuid || '';

  const [isSales, setIsSales] = useState(false);
  const [entries, setEntries] = useState<InstantEntry[]>([]);
  const [corner, setCorner] = useState<Corner>(DEFAULT_CORNER);
  // A Set in state, replaced (never mutated) so a change re-renders.
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  // Which uuid's stored state `corner`/`dismissed` currently hold. State, not a
  // ref, and the uuid rather than a boolean: a ref flipped inside the restore
  // effect is already true when the persist effect runs on the SAME commit, and
  // a boolean toggled false→true in one effect body batches to no change at
  // all, so on a uuid switch the outgoing rep's values would still be written
  // under the incoming rep's key.
  const [hydratedUuid, setHydratedUuid] = useState<string | null>(null);
  // Free position while a drag is in flight; null once it snaps to a corner.
  const [dragPos, setDragPos] = useState<Position | null>(null);
  const [now, setNow] = useState(() => new Date());

  const popupRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  // Task uuids already reported as shown, so a re-render, a relay packet for a
  // sibling task or a reconcile does not write a second row for the same
  // (rep, task). Scoped to this mount: a reload is a genuinely new impression.
  const shownRef = useRef(new Set<string>());
  // Wall-clock of the last reconcile REQUEST, for the staleness guard below.
  const lastReconcileRef = useRef(0);
  // Monotonic id per reconcile request. Two can be in flight at once (a forced
  // mount retry and a focus reconcile), and since removal is authoritative an
  // older response landing last would delete everything the newer one added.
  const reconcileSeqRef = useRef(0);

  // Restore the rep's corner and dismissals. Keyed on the uuid so a logout/login
  // in the same tab picks up the other rep's preferences instead of inheriting.
  useEffect(() => {
    const stored = readPopupState(cmsUserUuid);
    setCorner(stored.corner);
    setDismissed(new Set(stored.dismissed_task_uuids));
    setHydratedUuid(cmsUserUuid);
  }, [cmsUserUuid]);

  // The only writer. setState updaters must stay pure — persisting from inside
  // one double-writes under StrictMode and is a side effect React is free to
  // discard or replay under concurrent rendering. The gate holds until a render
  // carries this uuid's restored values, so the defaults are never written over
  // a stored blob.
  useEffect(() => {
    if (hydratedUuid !== cmsUserUuid) return;
    writePopupState(cmsUserUuid, {
      corner,
      dismissed_task_uuids: Array.from(dismissed)
    });
  }, [hydratedUuid, cmsUserUuid, corner, dismissed]);

  // Resolves true on a completed reconcile, false on a failed or superseded
  // one, so the mount retry below can tell "no tasks" from "no answer".
  const reconcile = useCallback((options?: { force?: boolean }): Promise<boolean> => {
    const force = !!(options && options.force);
    const startedAt = Date.now();
    if (!force && startedAt - lastReconcileRef.current < RECONCILE_STALE_MS) {
      return Promise.resolve(true);
    }
    lastReconcileRef.current = startedAt;
    reconcileSeqRef.current += 1;
    const seq = reconcileSeqRef.current;
    return getApi(getApiUrl(PENDING_URL), true)
      .then((res: PendingResponse) => {
        // Superseded: a newer request was issued after this one, so applying
        // this (authoritative) response would delete what the newer one added.
        // Resolved false, not true: this request answered nothing, and the
        // mount retry chain must keep going rather than treat it as the answer.
        if (seq !== reconcileSeqRef.current) return false;
        const data = res?.data?.data || {};
        setIsSales(!!data.is_sales);
        // The server excludes claimed rows; the filter guards an older server
        // that still listed them. A claimed task must never be re-raised.
        const rawItems = Array.isArray(data.items) ? data.items : [];
        const items = rawItems.filter((i) => !i.claimed_by_ops_uuid);
        // Counted BEFORE the claim filter, so an older server's claimed rows
        // still count toward the cap.
        const complete = rawItems.length < PENDING_LIMIT;
        // The REST list is authoritative for what is still pending: anything it
        // does not mention is gone, including a task whose `cleared` packet this
        // tab missed (oneshot relays are never replayed). Two exceptions: a
        // packet that landed while this request was in flight, which the server
        // could not have known about (hence arrived_at_ms), and a response that
        // may have been truncated by the server's row cap, where "absent" does
        // not mean "gone" — those still merge and update, they just remove
        // nothing.
        setEntries((prev) => {
          // Built from the RAW rows, not the filtered ones: "the server
          // mentioned this uuid" is what dedupe means here, so a prev entry the
          // response reports as claimed is dropped even under the cap, where
          // the truncation guard suppresses removal.
          const seen = new Set(rawItems.map((i) => i.task_uuid));
          return items
            .concat(
              prev.filter(
                (e) => !seen.has(e.task_uuid) && (!complete || (e.arrived_at_ms ?? 0) > startedAt)
              )
            )
            .sort(byAge);
        });
        return true;
      })
      .catch(
        () =>
          // A failed reconcile leaves whatever the relay has already delivered.
          // Silent on purpose: this runs on every focus, and a toast per failure
          // would be noise on a flaky connection.
          false
      );
  }, []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const attempt = (n: number) => {
      reconcile({ force: true }).then((ok) => {
        if (cancelled || ok || n >= MOUNT_RECONCILE_ATTEMPTS) return;
        retryTimer = setTimeout(() => attempt(n + 1), MOUNT_RECONCILE_RETRY_MS);
      });
    };
    attempt(1);
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      reconcile();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [reconcile]);

  // Its own relay listener rather than an extra branch in the app shell's
  // handler: the shell's switch owns the fresh-lead OS notification and the
  // Twilio call packets, and this component's lifecycle (mount/unmount) is what
  // should govern the subscription.
  useEffect(() => {
    const handler = (item: RelayItem) => {
      const payload = item?.data as InstantEntry | undefined;
      if (!payload) return;
      if (payload.type === 'instant_task') {
        if (!payload.task_uuid) return;
        // The fan-out targets SALES users only (SalesOpsUUIDs), so receiving one
        // of these packets IS the capability — enough to un-stick a session
        // whose mount reconcile never answered.
        setIsSales(true);
        setEntries((prev) => mergeEntries(prev, payload));
        return;
      }
      if (payload.type === 'instant_task_cleared') {
        if (!payload.task_uuid) return;
        setEntries((prev) => prev.filter((e) => e.task_uuid !== payload.task_uuid));
      }
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => {
      CallbackHandler.shared.unregisterRelayItemListener(handler);
    };
  }, []);

  // 'shown' is written when a task is actually rendered in the pop-up, not when
  // it arrives: a task that lands while the alert is dismissed, or for a
  // non-sales session, was never put in front of anyone.
  useEffect(() => {
    if (!isSales) return;
    entries.forEach((entry) => {
      const uuid = entry.task_uuid;
      if (!uuid || dismissed.has(uuid) || shownRef.current.has(uuid)) return;
      shownRef.current.add(uuid);
      sendAlertEvent(uuid, EVENT_SHOWN);
    });
  }, [entries, dismissed, isSales]);

  // One clock for every timer in the pop-up.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Pointer Events, so the pop-up drags with a finger or a pen as well as a
  // mouse. Pointer capture also means the move/up pair keeps arriving on the
  // handle after the cursor leaves it, which is what the old window-level
  // mousemove/mouseup listeners were for.
  const onDragMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const offset = dragOffsetRef.current;
    if (!offset) return;
    setDragPos({ left: event.clientX - offset.x, top: event.clientY - offset.y });
  }, []);

  const onDragEnd = useCallback(() => {
    if (!dragOffsetRef.current) return;
    dragOffsetRef.current = null;
    const node = popupRef.current;
    if (node) {
      setCorner(
        nearestCorner(node.getBoundingClientRect(), {
          width: window.innerWidth,
          height: window.innerHeight
        })
      );
    }
    setDragPos(null);
  }, []);

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Right- and middle-click must not start a drag: they deliver no matching
    // pointerup on the handle.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const node = popupRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    dragOffsetRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    setDragPos({ left: rect.left, top: rect.top });
    const handle = event.currentTarget;
    if (handle && handle.setPointerCapture && event.pointerId !== undefined) {
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        // Without capture a release outside the handle delivers neither
        // pointerup nor pointercancel, which would leave the pop-up stuck at its
        // free position and resume dragging on the next move with no button
        // down. Abort instead: the pop-up stays in its corner.
        dragOffsetRef.current = null;
        setDragPos(null);
      }
    }
  };

  const dismiss = () => {
    // One row per task per dismissal of a task the rep had not already
    // dismissed; re-dismissing the same pop-up after a new task arrives must not
    // re-count the older ones.
    entries
      .filter((e) => !dismissed.has(e.task_uuid))
      .forEach((e) => sendAlertEvent(e.task_uuid, EVENT_DISMISSED));
    setDismissed((prev) => {
      const next = new Set(prev);
      entries.forEach((e) => next.add(e.task_uuid));
      return next;
    });
  };

  // Dismissal hides the pop-up, it does not drop the tasks: a task dismissed
  // while others were pending is still counted and listed once a NEW task brings
  // the pop-up back, because it is still waiting for someone.
  const hasUndismissed = entries.some((e) => !dismissed.has(e.task_uuid));
  if (!isSales || entries.length === 0 || !hasUndismissed) return null;

  const multiple = entries.length > 1;
  const position: CSSProperties = dragPos
    ? { left: dragPos.left, top: dragPos.top }
    : cornerStyle(corner);

  const timerOf = (entry: InstantEntry) => {
    const ms = elapsedMs(entry.created_at_iso, now);
    return {
      text: ms === null ? '' : formatElapsed(ms),
      color: timerColorCss(ms === null ? 0 : ms)
    };
  };

  return (
    <div
      ref={popupRef}
      data-testid="instant-alert-popup"
      data-corner={corner}
      className="fixed rounded-lg border overflow-hidden"
      style={{
        ...position,
        width: POPUP_WIDTH_PX,
        zIndex: 1200,
        background: 'var(--surface-card)',
        borderColor: 'var(--state-warning)',
        boxShadow: 'var(--shadow-modal)'
      }}
    >
      {/* The header is the drag handle — the body holds the links, and dragging
          from a link would fight the click that opens the task. */}
      <div
        onPointerDown={startDrag}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
        className="flex items-center gap-2 px-3 py-2"
        style={{
          background: 'var(--state-warning-bg)',
          color: 'var(--state-warning-text)',
          cursor: 'move',
          userSelect: 'none',
          // Without this a touch drag scrolls the page instead of moving the
          // pop-up, and no pointermove is delivered.
          touchAction: 'none'
        }}
      >
        <span className="text-sm font-bold">
          {multiple ? `Instant Task Alert: ${entries.length} pending` : 'Instant Task Alert'}
        </span>
        <button
          type="button"
          aria-label="Dismiss instant task alert"
          onClick={dismiss}
          className="ml-auto text-base leading-none"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--state-warning-text)',
            cursor: 'pointer'
          }}
        >
          ×
        </button>
      </div>

      {multiple ? (
        <div>
          {entries.map((entry) => {
            const timer = timerOf(entry);
            return (
              <Link
                key={entry.task_uuid}
                to={`${FORM_PATH}?uuid=${encodeURIComponent(entry.task_uuid)}`}
                className="block px-3 py-2 border-t no-underline"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold">
                    {shortLabel(entry.task_type, entry.task_label)}
                  </span>
                  <span
                    className="ml-auto text-xs"
                    style={{
                      color: timer.color,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {timer.text}
                  </span>
                </div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {entry.student_name} · {assignedLine(entry)}
                </div>
              </Link>
            );
          })}
          <div className="px-3 py-2 border-t" style={{ borderColor: 'var(--border-default)' }}>
            <Link to={LIST_PATH} className="text-sm font-medium text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
        </div>
      ) : (
        (() => {
          const entry = entries[0];
          const timer = timerOf(entry);
          return (
            <div className="px-3 py-3">
              <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {shortLabel(entry.task_type, entry.task_label)}
              </div>
              <div className="text-sm capitalize" style={{ color: 'var(--text-primary)' }}>
                {entry.student_name}
              </div>
              {/* The reason is the single line that tells a rep why this jumped
                  the queue. Only the one-pending layout has room for it; the
                  compact list drops it, per the wireframe. */}
              <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                {[entry.reason, assignedLine(entry)].filter(Boolean).join(' · ')}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className="text-sm"
                  style={{
                    color: timer.color,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  ⏱ {timer.text}
                </span>
                <Link
                  to={`${FORM_PATH}?uuid=${encodeURIComponent(entry.task_uuid)}`}
                  className="ml-auto text-sm font-medium text-blue-600 hover:underline"
                >
                  Open task →
                </Link>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}

export default InstantAlertPopup;
